import { useMemo } from "react";

export interface ComparisonConfig {
  showChangeAs?: "value" | "percentage";
  showChangeValues?: boolean;
  changeColor?: string;
  timeUnit?: string;
}

export interface ComparisonResult {
  currentValue: number | null;
  previousValue: number | null;
  delta: number | null;
  percentChange: number | null;
}

export interface ComparisonValue {
  current: number | null;
  comparison: number | null;
  delta: number | null;
  percentage: number | null;
  raw: string;
}

export function parseComparisonValue(value: any): ComparisonValue {
  // If not a string with comma, or not a comparison value
  if (typeof value !== "string" || !value.includes(",")) {
    return {
      current: null,
      comparison: null,
      delta: null,
      percentage: null,
      raw: String(value),
    };
  }

  const [fromStr, toStr] = value.split(",");
  const current = fromStr === "__VZBL::null__" ? null : Number(fromStr);
  const comparison = toStr === "__VZBL::null__" ? null : Number(toStr);

  // Calculate delta and percentage
  let delta = null;
  let percentage = null;

  if (current !== null && comparison !== null) {
    delta = comparison - current;
    if (current === 0) {
      percentage = comparison > 0 ? 100 : comparison < 0 ? -100 : 0;
    } else {
      percentage = (delta / Math.abs(current)) * 100;
    }
  }

  return {
    current,
    comparison,
    delta,
    percentage,
    raw: value,
  };
}

const safeNumber = (value: string | null): number | null => {
  if (!value || value === "__VZBL::null__" || value === "n/a") return null;
  const num = Number(value.replace(/^\+/, "").replace(/%$/, ""));
  return Number.isFinite(num) ? num : null;
};

export const theTuple = (value: any): [string | null, string | null] => {
  if (!value) return [null, null];

  // Handle array inputs
  if (Array.isArray(value)) {
    const [current, previous] = value;
    return [current || null, previous || null];
  }

  // Convert to string and handle both comma and space separators
  const str = String(value);
  if (str === "null" || str === "__VZBL::null__") return [null, null];

  const separator = str.includes(",") ? "," : " ";
  const [current, previous] = str.split(separator);
  return [current?.trim() || null, previous?.trim() || null];
};

export const isComparisonValue = (value: any): boolean => {
  const [current, previous] = theTuple(value);
  return current !== null && previous !== null;
};

export function useComparisonValue(value: any): ComparisonResult {
  return useMemo(() => {
    // Parse tuple and convert to numbers
    const [currentStr, previousStr] = theTuple(value);
    const currentValue = safeNumber(currentStr);
    const previousValue = safeNumber(previousStr);

    // If either value is null, we can't calculate changes
    if (currentValue === null || previousValue === null) {
      return {
        currentValue,
        previousValue,
        delta: null,
        percentChange: null,
      };
    }

    // Calculate delta
    const delta = currentValue - previousValue;

    // Calculate percentage change with special cases
    let percentChange: number | null = null;
    if (previousValue === 0) {
      percentChange = currentValue === 0 ? 0 : currentValue > 0 ? 100 : -100;
    } else {
      percentChange = (delta / Math.abs(previousValue)) * 100;
    }

    return {
      currentValue,
      previousValue,
      delta,
      percentChange,
    };
  }, [value]);
}
